import { NutritionProgress } from "@/app/nutrition/plan";
import React, { MouseEventHandler, useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

export type NutritionProgressListItemProps = {
  progress: NutritionProgress;
  onClick: MouseEventHandler<HTMLLIElement>;
  onView: MouseEventHandler<HTMLParagraphElement>;
};

export const NutritionProgressListItem: FC<NutritionProgressListItemProps> = ({
  progress,
  onClick,
  onView,
}) => {
  const { t } = useTranslation();
  const itemClassNames = useMemo(
    () => ["opacity-50", "opacity-100", "opacity-50", "opacity-100"],
    []
  );

  const statusClassNames = useMemo(
    () => [
      "text-primary-600 bg-indigo-50 ring-indigo-500/10",
      "text-green-600 bg-green-50 ring-green-500/10",
      "",
      "text-yellow-600 bg-yellow-50 ring-yellow-500/10",
    ],
    []
  );

  const startDate = useMemo(() => {
    return DateTime.fromISO(progress.startDate).toFormat("dd-MM-yyyy");
  }, [progress]);

  const endDate = useMemo(() => {
    return DateTime.fromISO(progress.endDate).toFormat("dd-MM-yyyy");
  }, [progress]);

  return (
    <li
      onClick={onClick}
      key={`${progress.id}-${Math.floor(Math.random() * 100)}`}
      className={`${itemClassNames[progress.status]} flex flex-wrap items-center justify-between gap-x-6 py-5`}
    >
      <div className="min-w-0">
        <div className="flex items-start gap-x-3">
          <p
            className="text-sm font-semibold leading-6 text-gray-900 cursor-pointer"
            onClick={onView}
          >
            {progress.plan?.plan}
          </p>
          <p
            className={`${
              statusClassNames[progress.status]
            } mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset uppercase`}
          >
            {t(`contexts.nutrition.progress_status_${progress.status}`)}
          </p>
        </div>
        <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
          <p className="flex flex-nowrap gap-1">
            <time dateTime={progress.startDate}>{startDate}</time>
            <p>-</p>
            <time dateTime={progress.endDate}>{endDate}</time>
          </p>
          <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
            <circle r={1} cx={1} cy={1} />
          </svg>
          <p className="truncate">
            {t("lead.views.create_by")} {progress.assignedBy}
          </p>
        </div>
      </div>
    </li>
  );
};
